/**
 * Update Schedule — "Overdue" section. Two worlds per row, never mixed:
 *
 *   PLANNED (amber) — one span cell ("7/1 – 7/9 📅") with slide arrows under
 *   it. Click opens the re-plan calendar (PlanRangePicker); arrows slide the
 *   whole plan a workday at a time. Not-started rows move as a block (start
 *   push — duration held; a stretched finish is a duration change); started
 *   rows move the finish only (the pin); milestones move their point.
 *
 *   ACTUALS (green) — the classic date pickers capped at the data date +
 *   "as planned" pills, with the bulk pills in the toolbar above. Red means
 *   one thing: genuinely past due (effective plan at/behind the data date
 *   with no actual) — re-planning a row past the data date clears its red.
 *
 * Staging only — the parent commits on Proceed. The staged maps are owned by
 * the host; filter/search are local.
 */
import React from "react";
import { statusBadgeClass, statusLabel } from "../../../../utils/format";
import { isMilestoneTaskType } from "../../../../utils/p6";
import type { StatusActivity, StatusReviewResponse } from "../../../../models";
import { groupByWbs } from "../wbsGroup";
import { iso10, stepWorkday, workdayDelta, workdaysBetween } from "../dates";
import type { StagedPlan } from "../plan";
import { basePlanOf, isStarted, planEditability } from "../plan";
import ActualCell from "./ActualCell/ActualCell";
import PlanCell from "./PlanCell/PlanCell";
import PlanRangePicker from "./PlanRangePicker/PlanRangePicker";
import type { Staged } from "../staged";
import "./OverdueSection.css";

interface Props {
  data: StatusReviewResponse;
  staged: Record<number, Staged>;
  stagedPlans: Record<number, StagedPlan>;
  onStageActual: (id: number, side: "start" | "finish", date: string) => void;
  onStagePlan: (id: number, plan: StagedPlan) => void;
  onUnstagePlan: (id: number) => void;
  onUnstageSlot: (id: number, side: "start" | "finish") => void;
  committing: boolean;
}

const NUM_COLS = 7;

const OverdueSection: React.FC<Props> = ({
  data,
  staged,
  stagedPlans,
  onStageActual,
  onStagePlan,
  onUnstagePlan,
  onUnstageSlot,
  committing,
}) => {
  const [query, setQuery] = React.useState("");
  const [pickerFor, setPickerFor] = React.useState<number | null>(null);
  // The clicked PlanCell span — the picker popover anchors beneath it.
  const pickerTriggerRef = React.useRef<HTMLElement | null>(null);
  const dataDate = iso10(data.data_date);

  // Always overdue-only — current/on-time work belongs in In Progress.
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.activities.filter((a) => {
      if (!a.start_overdue && !a.finish_overdue) return false;
      if (q && !a.task_name.toLowerCase().includes(q) && !a.task_code.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data, query]);

  const rows = React.useMemo(() => groupByWbs(filtered), [filtered]);

  const counts = React.useMemo(() => {
    const list = data.activities;
    return {
      total: list.length,
      starts: list.filter((a) => a.start_overdue).length,
      finishes: list.filter((a) => a.finish_overdue).length,
    };
  }, [data]);

  /** Effective plan for a row — the staged re-plan, else the committed one. */
  const effPlanOf = (a: StatusActivity): StagedPlan =>
    stagedPlans[a.activity_id] ?? basePlanOf(a);

  // A side is genuinely past due when its EFFECTIVE plan sits at/behind the
  // data date with no actual — a staged re-plan past the data date clears it.
  const startLate = (a: StatusActivity): boolean =>
    a.start_overdue
    && !(staged[a.activity_id]?.start ?? iso10(a.actual_start))
    && effPlanOf(a).start <= dataDate;
  const finishLate = (a: StatusActivity): boolean =>
    a.finish_overdue
    && !(staged[a.activity_id]?.finish ?? iso10(a.actual_finish))
    && effPlanOf(a).finish <= dataDate;

  // Overdue sides not yet resolved (no actual, still at/behind the data date)
  // — the bulk targets.
  const unset = React.useMemo(() => {
    const starts: StatusActivity[] = [];
    const finishes: StatusActivity[] = [];
    for (const a of data.activities) {
      if (startLate(a) && effPlanOf(a).start) starts.push(a);
      if (finishLate(a) && effPlanOf(a).finish) finishes.push(a);
    }
    return { starts, finishes };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, staged, stagedPlans, dataDate]);

  // A not-yet-started activity's FINISH implies it also STARTED — the commit
  // injects the start anyway, so stage it too (otherwise the start cell stays
  // red and the row reads "overdue" even though Apply will complete it). And
  // undoing either side clears the whole completion: a finish can't stand
  // without a start, so a half-undo would otherwise leave the row "started".
  const stageFinish = (a: StatusActivity, d: string) => {
    onStageActual(a.activity_id, "finish", d);
    if (!iso10(a.actual_start) && !staged[a.activity_id]?.start) {
      onStageActual(a.activity_id, "start", iso10(a.start) || d);
    }
  };
  const unstageRow = (a: StatusActivity, side: "start" | "finish") => {
    onUnstageSlot(a.activity_id, side);
    if (iso10(a.actual_start)) return; // already started — the sides are independent
    // Clear the coupled side too, but ONLY when it still holds the auto-staged
    // planned value — never a date the user deliberately typed.
    const other = side === "start" ? "finish" : "start";
    const otherStaged = staged[a.activity_id]?.[other];
    const otherPlanned = iso10(other === "start" ? a.start : a.finish);
    if (otherStaged !== undefined && otherStaged === otherPlanned) {
      onUnstageSlot(a.activity_id, other);
    }
  };

  const markAllStarts = () => unset.starts.forEach((a) => onStageActual(a.activity_id, "start", effPlanOf(a).start));
  const markAllFinishes = () => unset.finishes.forEach((a) => stageFinish(a, effPlanOf(a).finish));

  const pickerAct = pickerFor != null
    ? data.activities.find((a) => a.activity_id === pickerFor) ?? null
    : null;
  const pickerEdit = pickerAct
    ? planEditability(pickerAct, data.use_expect_end,
        staged[pickerAct.activity_id]?.start, staged[pickerAct.activity_id]?.finish)
    : null;

  /** Slide the effective plan ±1 workday (whole block; finish-only for started
   *  rows). Never lands a plan at/behind the data date. */
  const slidePlan = (a: StatusActivity, dir: 1 | -1) => {
    const edit = planEditability(a, data.use_expect_end,
      staged[a.activity_id]?.start, staged[a.activity_id]?.finish);
    const eff = effPlanOf(a);
    if (edit.mode === "finish") {
      const nf = stepWorkday(eff.finish, dir);
      if (dir < 0 && nf <= dataDate) return;
      onStagePlan(a.activity_id, { start: eff.start, finish: nf });
      return;
    }
    if (edit.mode === "none") return;
    const ns = stepWorkday(eff.start, dir);
    if (dir < 0 && ns <= dataDate) return;
    onStagePlan(a.activity_id, {
      start: ns,
      finish: edit.mode === "point" ? ns : stepWorkday(eff.finish, dir),
    });
  };

  return (
    <div className="ovt">
      <div className="ovt-toolbar">
        <span className="ovt-summary">
          <b>{counts.total}</b> live
          <span className="ovt-sep">·</span>
          <span className="ovt-od"><b>{counts.starts}</b> overdue starts</span>
          <span className="ovt-sep">·</span>
          <span className="ovt-od"><b>{counts.finishes}</b> overdue finishes</span>
        </span>
        <input
          className="ovt-search"
          type="text"
          placeholder="Search id / name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {/* Bulk "as planned" buttons sit over the Actual columns —
            where you actually set the dates — not off to the left. */}
        <button
          className="ovt-bulk"
          disabled={committing || unset.starts.length === 0}
          onClick={markAllStarts}
          title="Set every overdue start to its planned date"
        >
          Mark {unset.starts.length} start{unset.starts.length === 1 ? "" : "s"} as planned
        </button>
        <button
          className="ovt-bulk"
          disabled={committing || unset.finishes.length === 0}
          onClick={markAllFinishes}
          title="Set every overdue finish to its planned date"
        >
          Mark {unset.finishes.length} finish{unset.finishes.length === 1 ? "" : "es"} as planned
        </button>
      </div>

      <div className="srm-table-wrap">
        <table className="srm-table">
          <colgroup>
            <col className="srm-c-id" />
            <col className="srm-c-name" />
            <col className="srm-c-status" />
            <col className="srm-c-num" />
            <col className="srm-c-plan" />
            <col className="srm-c-actdate" />
            <col className="srm-c-actdate" />
          </colgroup>
          <thead>
            <tr>
              <th>Activity ID</th>
              <th>Activity Name</th>
              <th>Status</th>
              <th className="srm-r">Rem</th>
              <th className="srm-c" title="The planned span — click it to re-plan on a calendar; ◀ ▶ slides it a workday at a time">Planned</th>
              <th className="srm-ga" title="What actually happened — on/before the data date">Actual Start</th>
              <th className="srm-ga" title="What actually happened — on/before the data date">Actual Finish</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={NUM_COLS} className="srm-empty">No overdue activities{query ? " match the search" : ""}.</td></tr>
            )}
            {rows.map((r) => {
              if (r.kind === "wbs") {
                return (
                  <tr key={r.key} className={`srm-wbs srm-depth-${Math.min(r.depth, 3)}`}>
                    <td colSpan={NUM_COLS} style={{ paddingLeft: r.depth * 18 + 8 }}>
                      <strong>{r.name}</strong>
                      <span className="srm-count">{r.count}</span>
                    </td>
                  </tr>
                );
              }
              const a = r.act;
              const id = a.activity_id;
              const s = staged[id];
              const base = basePlanOf(a);
              const plan = stagedPlans[id];
              const eff = plan ?? base;
              const edit = planEditability(a, data.use_expect_end, s?.start, s?.finish);
              const milestoneRow = isMilestoneTaskType(a.task_type);
              const movedPlan = plan !== undefined
                && (plan.start !== base.start || plan.finish !== base.finish);
              const held = a.cstr_pushed || !!a.expect_end_date;
              const sLate = startLate(a);
              const fLate = finishLate(a);
              const startedRow = isStarted(a, s?.start);
              const slideAnchor = edit.mode === "finish" ? eff.finish : eff.start;
              const canSlideBack = slideAnchor !== "" && stepWorkday(slideAnchor, -1) > dataDate;
              // Signed slide distance of the staged move; "±" = duration-only.
              const anchorDelta = edit.mode === "finish"
                ? workdayDelta(base.finish, eff.finish)
                : workdayDelta(base.start, eff.start);
              const deltaLabel = !movedPlan
                ? "—"
                : anchorDelta === 0
                  ? "±"
                  : `${anchorDelta > 0 ? "+" : "−"}${Math.abs(anchorDelta)}d`;
              const effDuration = milestoneRow
                ? 0
                : !startedRow && plan
                  ? workdaysBetween(plan.start, plan.finish)
                  : Math.max(0, Math.round(a.remaining_duration));
              const durationEdited = !startedRow && plan !== undefined
                && effDuration !== Math.max(1, Math.round(a.remaining_duration));
              return (
                <tr key={r.key} className={`srm-row${(sLate || fLate) ? " is-overdue" : ""}`}>
                  <td className="srm-mono">{a.task_code}</td>
                  <td className="srm-name">{a.task_name}</td>
                  <td><span className={statusBadgeClass(a.status)}>{statusLabel(a.status)}</span></td>
                  <td className={`srm-r srm-mono${durationEdited ? " srm-expected" : ""}`}>{effDuration}d</td>
                  <td className="srm-plan-cell">
                    <PlanCell
                      start={eff.start}
                      finish={eff.finish}
                      point={milestoneRow}
                      startLocked={startedRow}
                      moved={movedPlan}
                      held={held}
                      deltaLabel={deltaLabel}
                      canEdit={edit.mode !== "none"}
                      reason={edit.reason}
                      canSlideBack={canSlideBack}
                      disabled={committing}
                      onOpen={(el) => {
                        pickerTriggerRef.current = el;
                        setPickerFor(id);
                      }}
                      onSlide={(dir) => slidePlan(a, dir)}
                      onReset={() => onUnstagePlan(id)}
                    />
                  </td>
                  <td className={`srm-date-cell${sLate ? " srm-late-cell" : ""}`}>
                    {iso10(a.actual_start) && s?.start === undefined ? (
                      <span className="srm-committed">✓ {iso10(a.actual_start)}<span className="srm-committed-lbl">started</span></span>
                    ) : (
                      <ActualCell
                        staged={s?.start}
                        committed={iso10(a.actual_start)}
                        planned={eff.start}
                        late={sLate}
                        dataDate={dataDate}
                        disabled={committing}
                        label="start"
                        onStage={(d) => onStageActual(id, "start", d)}
                        onUnstage={() => unstageRow(a, "start")}
                      />
                    )}
                  </td>
                  <td className={`srm-date-cell${fLate ? " srm-late-cell" : ""}`}>
                    <ActualCell
                      staged={s?.finish}
                      committed={iso10(a.actual_finish)}
                      planned={eff.finish}
                      late={fLate}
                      dataDate={dataDate}
                      disabled={committing}
                      label="finish"
                      onStage={(d) => stageFinish(a, d)}
                      onUnstage={() => unstageRow(a, "finish")}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pickerAct && pickerEdit && pickerEdit.mode !== "none" && (
        <PlanRangePicker
          key={pickerAct.activity_id}
          code={pickerAct.task_code}
          name={pickerAct.task_name}
          mode={pickerEdit.mode}
          plan={effPlanOf(pickerAct)}
          lockedStart={iso10(pickerAct.actual_start) || staged[pickerAct.activity_id]?.start || ""}
          dataDate={dataDate}
          moved={stagedPlans[pickerAct.activity_id] !== undefined}
          triggerRef={pickerTriggerRef}
          onChange={(p) => onStagePlan(pickerAct.activity_id, p)}
          onSlide={(dir) => slidePlan(pickerAct, dir)}
          onReset={() => onUnstagePlan(pickerAct.activity_id)}
          onClose={() => setPickerFor(null)}
        />
      )}
    </div>
  );
};

export default OverdueSection;
